import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Permission } from '@shared/infrastructure/auth/permission.js';
import { sessionApi } from '@shared/infrastructure/auth/session.api.js';
import { ApiError } from '@shared/infrastructure/http/api-error.js';

export interface SessionGroup {
  id: string;
  name: string;
}

export interface Session {
  email: string;
  group: SessionGroup | null;
  permissions: readonly string[];
}

type Status = 'unknown' | 'authenticated' | 'anonymous';

/**
 * User session.
 *
 * It lives in Pinia and not in vue-query because it is not server state that
 * gets queried and refreshed: it is the boot state the guard and the shell
 * depend on. `ensureLoaded` asks **only once** per page load; navigating around
 * does not bother the backend again.
 *
 * It also carries the permissions, and that is what makes `can()` cheap enough
 * to call from a template: no request, no async, just a lookup.
 */
export const useAuthStore = defineStore('auth', () => {
  const session = ref<Session | null>(null);
  const status = ref<Status>('unknown');
  let inFlight: Promise<void> | null = null;

  const permissions = computed<readonly string[]>(() => session.value?.permissions ?? []);

  /**
   * Whether the session holds a permission.
   *
   * *Fail-closed*: no session, or permissions the backend did not send, means
   * no. Hiding something that would answer 403 is the point; the backend is
   * still the one enforcing it.
   */
  function can(permission: Permission): boolean {
    return permissions.value.includes(permission);
  }

  async function ensureLoaded(): Promise<void> {
    if (status.value !== 'unknown') return;

    inFlight ??= (async () => {
      try {
        session.value = await load();
        status.value = 'authenticated';
      } catch (error) {
        if (!(error instanceof ApiError)) throw error;
        session.value = null;
        status.value = 'anonymous';
      } finally {
        inFlight = null;
      }
    })();

    await inFlight;
  }

  async function loginWithGoogle(code: string, redirectUri: string): Promise<void> {
    await sessionApi.loginWithGoogle(code, redirectUri);
    // The login answers who came in; `me` answers what they can do. Asking
    // again is one request and keeps the permissions in a single place.
    session.value = await load();
    status.value = 'authenticated';
  }

  /**
   * Re-reads the session.
   *
   * Editing a group changes what the people in it can do, and the person editing
   * is usually in one. Without this, the sidebar and the buttons keep answering
   * from the permissions this tab loaded at boot until someone reloads the page.
   */
  async function refresh(): Promise<void> {
    try {
      session.value = await load();
      status.value = 'authenticated';
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      reset();
    }
  }

  async function logout(): Promise<void> {
    try {
      await sessionApi.logout();
    } finally {
      // Even if the server fails, inside this tab the session is over.
      reset();
    }
  }

  function reset(): void {
    session.value = null;
    status.value = 'anonymous';
  }

  async function load(): Promise<Session> {
    const response = await sessionApi.me();

    // The fallbacks are not paranoia about types: an older API answering this
    // route without the fields lands here, and no permissions has to mean
    // nothing is allowed rather than `undefined.includes` blowing up the guard.
    return {
      email: response.email,
      group: response.group ?? null,
      permissions: response.permissions ?? [],
    };
  }

  return {
    session,
    status,
    permissions,
    can,
    ensureLoaded,
    refresh,
    loginWithGoogle,
    logout,
    reset,
  };
});
